import { useAuth } from '@clerk/react';
/**
 * Billing — Polar-backed subscription + entitlement helpers.
 *
 * Phase 6 of the Polar migration flipped this module off Clerk Billing
 * entirely. Clerk stays for identity (JWT, orgs, memberships). The
 * subscription / checkout / plan-change / portal flow now goes through
 * our own `/v1/billing/*` surface (Phase 4) which talks to Polar
 * server-to-server.
 *
 * What lives here:
 *
 *   - `useSubscription()` — `GET /v1/billing/subscription`. Single
 *     source of truth for "is there a Polar subscription, and what
 *     state is it in". The Settings → Billing page switches on this.
 *   - `useBilling()` — cheap read that joins `useWorkspace()` +
 *     `useUsage()`. Stable shape for the sidebar pill + 402 handler.
 *     Keeps working when Polar is down because both underlying reads
 *     degrade gracefully.
 *   - `useCheckoutMutation()`       — `POST /v1/billing/checkout`.
 *   - `useChangePlanMutation()`     — `POST /v1/billing/change-plan`.
 *   - `useOpenPortalMutation()`     — `GET  /v1/billing/portal`.
 *   - `useBillingPlans()`           — static plan-card catalogue
 *     (marketing copy only, no billing ids). Limits come out of
 *     `plan_limits` at runtime via `useEntitlement`.
 *   - `useEntitlement()` / `hasEntitlement()` — plan_limits-driven
 *     gates. No Clerk `has({plan})` call anywhere — the server writes
 *     `plan_limits` from the Polar webhook, so our own JWT claim
 *     converges via that column and we don't need a second source.
 *   - `planChangeDirection()` — same signature as before, used by
 *     the Plan grid to label CTAs upgrade vs downgrade.
 *
 * No more Clerk experimental billing helpers (CheckoutButton,
 * SubscriptionDetailsButton) or Clerk plan-id constants. The
 * post-§9.24 "no free tier" rule still holds: `plan === null`
 * means the org lost its subscription (or never had one) and
 * renders with `EMPTY_PLAN_LIMITS` semantics.
 */
import type { QueryClient, UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api';
import type {
  BillingPortalResponse,
  BillingSubscriptionResponse,
  ChangePlanBody,
  ChangePlanResponse,
  CheckoutBody,
  CheckoutResponse,
} from '../api-types';
import { useActiveOrg } from './active-org';
import { billingKeys } from './query-keys';
import { invalidateUsage, useUsage } from './usage';
import { invalidateWorkspace, useWorkspace } from './workspace';

// ─── Types ──────────────────────────────────────────────────────────

/**
 * The four Polar-backed plans. Matches `BillingPlanSlug` in
 * api-types.ts and `POLAR_PLAN_SLUGS` in `@clickweave/shared`. Kept
 * local here because dashboard-v2 doesn't pull in the shared package
 * (would drag zod + the server PolarClient into the browser bundle).
 */
export const PLAN_SLUGS = ['indie', 'startup', 'pro', 'business'] as const;
export type PlanSlug = (typeof PLAN_SLUGS)[number];

export interface BillingPlanOption {
  id: PlanSlug;
  name: string;
  description: string;
  cta: 'upgrade' | 'contact';
  /** Human-friendly price label, e.g. "$19 / month". */
  price_label: string;
  features: string[];
}

export interface BillingSummary {
  plan: string | null;
  plan_limits: PlanLimitsShape;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Shape of the `plan_limits` jsonb blob surfaced by
 * `/v1/workspace`, `/v1/usage`, and `/v1/billing/subscription`.
 * Mirrors `PlanLimits` in `packages/db/src/plan-limits.ts`.
 *
 * Phase 5b renamed three fields to signal they're *included
 * allowances*, not hard caps (monitors + seats above the allowance
 * are billed via Polar meters, not blocked). The old names stay as
 * optional aliases for one release while the server-side migration
 * rewrites live rows.
 *
 * Kept open (`Record<string, unknown>` would be safer but loses
 * autocomplete; full type is more ergonomic). Any field can be
 * missing on suspended orgs.
 */
export interface PlanLimitsShape {
  // Canonical shape (Phase 5b).
  included_daily_monitors?: number;
  included_hourly_monitors?: number;
  max_on_demand_per_month?: number;
  included_seats?: number;
  retention_months?: number;
  api_access?: boolean;
  custom_headers?: boolean;
  alert_channels?: Array<'email' | 'slack' | 'webhook'>;
  max_concurrent_snapshots?: number;
  /** Phase 5: allow meter-billed overage above included allowances. */
  overage_allowed?: boolean;
  // Legacy names — still present on rows written pre-Phase-5b and
  // returned by the API's PassThrough schema. Readers should prefer
  // the `included_*` variants above.
  max_daily_monitors?: number;
  max_hourly_monitors?: number;
  max_team_members?: number;
  [key: string]: unknown;
}

// ─── Reads ──────────────────────────────────────────────────────────

/**
 * `GET /v1/billing/subscription` — the combined Supabase + Polar
 * block. Keyed by the active JWT-claim org (via `useActiveOrg`) so
 * an org switch drops the previous org's subscription cleanly.
 *
 * Enabled only once Clerk resolves + there's an active org. The
 * backend joins our `organisations` row with a live Polar read; if
 * Polar is unreachable the response still comes back with the
 * `polar.*` block nulled out — no need for the client to fall back.
 */
export function useSubscription(): UseQueryResult<BillingSubscriptionResponse> {
  const { isLoaded, isSignedIn } = useAuth();
  const { orgId } = useActiveOrg();
  return useQuery({
    queryKey: [...billingKeys.current(), orgId ?? null] as const,
    queryFn: () => apiFetch<BillingSubscriptionResponse>('/v1/billing/subscription'),
    enabled: isLoaded && !!isSignedIn && !!orgId,
  });
}

export function invalidateSubscription(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({ queryKey: billingKeys.all });
}

/**
 * Read-only billing summary. Joins `useWorkspace` + `useUsage` so
 * consumers don't need to know the plan lives on two surfaces.
 *
 * Keyed off the JWT-claim org via `useActiveOrg()` so the sidebar
 * pill + billing page + entitlement hook all read from the SAME
 * org row. Mixing claim-keyed and SDK-keyed org reads was the
 * historical source of plan-display inconsistency during token
 * rotation (see `query-keys.ts:workspaceKeys.current`).
 */
export function useBilling(): BillingSummary {
  const { orgId } = useActiveOrg();
  const workspace = useWorkspace();
  const usage = useUsage();

  const activeOrg = workspace.data?.organisations.find((o) => o.auth_provider_id === orgId);

  return {
    plan: usage.data?.plan ?? activeOrg?.plan ?? null,
    plan_limits: (usage.data?.plan_limits ?? activeOrg?.plan_limits ?? {}) as PlanLimitsShape,
    isLoading: workspace.isLoading || usage.isLoading,
    error: (workspace.error ?? usage.error ?? null) as Error | null,
  };
}

// ─── Mutations ──────────────────────────────────────────────────────

/**
 * `POST /v1/billing/checkout` — kicks off a Polar checkout session
 * for a **first-time** subscription. Returns the hosted checkout
 * URL; the caller is responsible for navigating the user there
 * (`window.location.assign` or an `<a>` click).
 *
 * Do NOT call for plan changes on an already-subscribed org — the
 * backend answers 409 `CONFLICT` with `meta.reason === 'ALREADY_SUBSCRIBED'`.
 * Use `useChangePlanMutation()` instead.
 *
 * The dashboard chooses its own `success_url` + `cancel_url`
 * (must be on the dashboard origin; enforced server-side). Both
 * land back on `/settings/billing?polar_checkout=success|cancel`
 * so the page can render a banner + refetch subscription state.
 */
export function useCheckoutMutation(): UseMutationResult<CheckoutResponse, Error, CheckoutBody> {
  return useMutation({
    mutationFn: (body: CheckoutBody) =>
      apiFetch<CheckoutResponse>('/v1/billing/checkout', {
        method: 'POST',
        body,
      }),
  });
}

/**
 * `POST /v1/billing/change-plan` — immediate upgrade or downgrade
 * on an already-subscribed org. Polar bills (or credits) the
 * proration diff right away; the `subscription.updated` webhook
 * lands within ≤ 10 s and rewrites our `plan` + `plan_limits`
 * columns. The dashboard page polls `/v1/usage` to catch the new
 * plan before showing a "refresh" toast on timeout.
 *
 * Invalidating after success nudges the sidebar pill; the poll
 * handles the billing-page self-refresh.
 */
export function useChangePlanMutation(): UseMutationResult<
  ChangePlanResponse,
  Error,
  ChangePlanBody
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ChangePlanBody) =>
      apiFetch<ChangePlanResponse>('/v1/billing/change-plan', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void invalidateSubscription(qc);
      void invalidateWorkspace(qc);
      void invalidateUsage(qc);
    },
  });
}

/**
 * `GET /v1/billing/portal` — short-lived Polar customer-portal URL.
 * Used for payment methods, invoices, and cancellation. Opened in a
 * new tab; the page doesn't need to refetch state on return because
 * Polar-portal changes fire webhooks that update our DB.
 *
 * Exposed as a mutation (not a query) because the portal URL expires
 * quickly — stale-time caching it would hand out dead links. The
 * caller invokes it on click, awaits the URL, opens it.
 */
export function useOpenPortalMutation(): UseMutationResult<BillingPortalResponse, Error, void> {
  return useMutation({
    mutationFn: () => apiFetch<BillingPortalResponse>('/v1/billing/portal'),
  });
}

// ─── Plan catalogue (marketing copy) ────────────────────────────────

/**
 * Hard-coded plan catalogue. Marketing copy only — the single
 * source of truth for numeric limits is `PLAN_LIMITS_BY_PLAN` in
 * `packages/db/src/plan-limits.ts`. Prices live here (commercial
 * concern, not engineering concern).
 *
 * No Polar ids on purpose: the server resolves `plan` → Polar
 * product id from its own env (`POLAR_PRODUCT_ID_*`). The client
 * sends a plan slug and the server picks the right product.
 */
const PLAN_CATALOGUE: BillingPlanOption[] = [
  {
    id: 'indie',
    name: 'Indie',
    description: 'Daily monitors, dashboard only.',
    cta: 'upgrade',
    price_label: '$19 / month',
    features: [
      '5 daily monitors included (+$3/mo each above)',
      '100 on-demand audits included (+$0.10 each above)',
      '1 seat included (+$15/seat/mo above)',
      '3 months retention',
      'Email alerts',
    ],
  },
  {
    id: 'startup',
    name: 'Startup',
    description: 'More daily monitors + Slack alerts.',
    cta: 'upgrade',
    price_label: '$49 / month',
    features: [
      '20 daily monitors included (+$3/mo each above)',
      '300 on-demand audits included (+$0.10 each above)',
      '5 seats included (+$15/seat/mo above)',
      '6 months retention',
      'Email + Slack alerts',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Hourly monitors, API access, webhooks.',
    cta: 'upgrade',
    price_label: '$149 / month',
    features: [
      '50 daily + 5 hourly monitors included',
      '+$3/mo per additional daily monitor · +$15/mo per hourly',
      '1,000 on-demand audits included (+$0.10 each above)',
      '15 seats included (+$15/seat/mo above)',
      '12 months retention',
      'API access + custom headers',
      'Email + Slack + webhook alerts',
    ],
  },
  {
    id: 'business',
    name: 'Business',
    description: 'Per-contract limits, support SLA.',
    cta: 'contact',
    price_label: 'Contact sales',
    features: [
      'Custom included allowances or hard-cap (contract-level)',
      'Priority queue + dedicated support',
      '24 months retention',
      'SOC 2 report on request',
    ],
  },
];

/**
 * Returns the plan catalogue synchronously. Declared as a hook (not
 * a constant) so a future `GET /v1/billing/plans` swap is a one-line
 * diff.
 */
export function useBillingPlans(): BillingPlanOption[] {
  return PLAN_CATALOGUE;
}

// ─── Plan-rank helpers ──────────────────────────────────────────────

/**
 * Plan slugs in ascending-entitlement order. Used by
 * `planChangeDirection()` for "upgrade" vs "downgrade" labelling.
 * Mirrors `planRank()` in `@clickweave/shared`.
 */
const PLAN_RANK = {
  indie: 1,
  startup: 2,
  pro: 3,
  business: 4,
} as const satisfies Record<PlanSlug, number>;

function rankOf(plan: string | null | undefined): number {
  if (!plan) return 0;
  const slug = plan.toLowerCase();
  return (PLAN_RANK as Record<string, number | undefined>)[slug] ?? 0;
}

/**
 * Compare two plan slugs from the user's POV. Used to label the
 * change-plan CTA on each card.
 *
 * Returns:
 *   - `upgrade`   → target is a higher tier than current
 *   - `downgrade` → target is a lower tier than current
 *   - `same`      → identical tier
 *   - `none`      → user has no active plan (suspended / never subscribed);
 *                   every paid target is effectively an upgrade, but the
 *                   billing page routes these through `useCheckoutMutation`
 *                   (first-time flow) not `useChangePlanMutation`.
 */
export function planChangeDirection(
  currentPlan: string | null | undefined,
  targetPlan: string | null | undefined,
): 'upgrade' | 'downgrade' | 'same' | 'none' {
  const current = rankOf(currentPlan);
  const target = rankOf(targetPlan);
  if (current === 0) return 'none';
  if (current === target) return 'same';
  return target > current ? 'upgrade' : 'downgrade';
}

// ─── Entitlements ───────────────────────────────────────────────────

/**
 * Known entitlement capabilities. Each maps to a concrete feature
 * the dashboard wants to gate pre-submit. Mirrors the server-side
 * enforcement — if the client-side gate is wrong the server still
 * refuses with a 402, so this hook is UX polish, not security.
 *
 * Mapping (Phase 6):
 *
 *   | capability        | plan_limits check |
 *   |-------------------|-------------------|
 *   | `create_api_key`  | `api_access === true` |
 *   | `create_monitor`  | `max_daily_monitors > 0` OR `max_hourly_monitors > 0` |
 *   | `monitor_hourly`  | `max_hourly_monitors > 0` |
 *   | `invite_member`   | `max_team_members > 1` (seat cap on indie=1) |
 *   | `webhooks`        | `alert_channels` includes `'webhook'` |
 *   | `integrations`    | `alert_channels` includes `'slack'` (Slack is the only wired one today) |
 *   | `alert_webhook`   | alias for `webhooks`, clearer for alert-channel UI |
 *   | `alert_slack`     | alias for `integrations`, clearer for alert-channel UI |
 *
 * Previous phases gated these by plan-rank comparison (`≥ indie`,
 * `≥ pro`, …). Rank comparisons are brittle when Business contracts
 * get custom `plan_limits` — Phase 6 reads the limit column directly
 * so "Business without webhooks" and "Business with custom seat
 * count" both work.
 */
export type Entitlement =
  | 'create_api_key'
  | 'create_monitor'
  | 'monitor_hourly'
  | 'invite_member'
  | 'webhooks'
  | 'integrations'
  | 'alert_webhook'
  | 'alert_slack';

export interface EntitlementResult {
  allowed: boolean;
  isLoading: boolean;
  reason: 'loading' | 'no_subscription' | 'feature_not_in_plan' | 'allowed';
  /** The org's current plan, if any. */
  currentPlan: string | null;
  /**
   * Lower bound on the plan tier required to unlock this entitlement,
   * as a plan slug. Consumers render this in upsell copy ("Upgrade
   * to Pro to schedule hourly monitors"). Resolved statically from
   * `MIN_PLAN_FOR` below — a rough guide for the CTA label only;
   * the actual gate is the plan_limits check, not this slug.
   */
  minPlan: PlanSlug;
}

/**
 * Advisory mapping of capability → the lowest public plan that
 * includes it. Used only for upsell copy ("Upgrade to Pro"). The
 * real gate is `hasEntitlement()` reading `plan_limits`.
 */
const MIN_PLAN_FOR: Record<Entitlement, PlanSlug> = {
  create_api_key: 'pro',
  create_monitor: 'indie',
  monitor_hourly: 'pro',
  invite_member: 'startup',
  webhooks: 'pro',
  integrations: 'startup',
  alert_webhook: 'pro',
  alert_slack: 'startup',
};

/**
 * Pure, testable capability check against a `plan_limits` blob.
 * Every `useEntitlement()` call routes through this; test helpers
 * can call it directly without mocking React Query.
 *
 * `null` / missing fields → deny. We never infer "missing means
 * allowed" — the server's `EMPTY_PLAN_LIMITS` fallback sets every
 * numeric field to 0 and every boolean to false for suspended
 * orgs, so "missing" only happens on the initial load frame.
 */
export function hasEntitlement(
  limits: PlanLimitsShape | null | undefined,
  capability: Entitlement,
): boolean {
  if (!limits) return false;
  switch (capability) {
    case 'create_api_key':
      return limits.api_access === true;
    case 'create_monitor':
      return (
        (limits.included_daily_monitors ?? limits.max_daily_monitors ?? 0) > 0 ||
        (limits.included_hourly_monitors ?? limits.max_hourly_monitors ?? 0) > 0
      );
    case 'monitor_hourly':
      return (limits.included_hourly_monitors ?? limits.max_hourly_monitors ?? 0) > 0;
    case 'invite_member':
      // A seat cap of 1 means "just the owner" — no invitations.
      // Under Phase 5b, any plan with `overage_allowed=true` lets
      // invites through regardless of the included seat allowance
      // (extra seats bill via the `extra_seats` meter). Only hard-
      // capped contracts (overage_allowed=false) still rely on the
      // raw seat count here.
      if (limits.overage_allowed === true) return true;
      return (limits.included_seats ?? limits.max_team_members ?? 0) > 1;
    case 'webhooks':
    case 'alert_webhook':
      return limits.alert_channels?.includes('webhook') === true;
    case 'integrations':
    case 'alert_slack':
      return limits.alert_channels?.includes('slack') === true;
    default: {
      // exhaustiveness guard — TS will error here if a new
      // Entitlement variant is added without a case.
      const never: never = capability;
      return never;
    }
  }
}

/**
 * Plan-aware entitlement gate for React components. Drives
 * `<UpsellInline />` surfaces on every pre-submit control.
 *
 * ```tsx
 * const { allowed, minPlan } = useEntitlement('monitor_hourly');
 * return allowed
 *   ? <HourlyScheduleSelect />
 *   : <UpsellInline feature="monitor_hourly" minPlan={minPlan} />;
 * ```
 *
 * No Clerk `has({plan})` call anywhere — Phase 6 removed the whole
 * "OR-the-two-sources" dance. The server writes `plan_limits` from
 * the Polar webhook within ≤ 10 s of any subscription event; after
 * that our `/v1/workspace` + `/v1/usage` reads are authoritative,
 * and a 30 s react-query stale-time plus the billing-page poll
 * cover the transition window.
 */
export function useEntitlement(capability: Entitlement): EntitlementResult {
  const billing = useBilling();

  const currentPlan = billing.plan;
  const minPlan = MIN_PLAN_FOR[capability];

  if (billing.isLoading) {
    return {
      allowed: false,
      isLoading: true,
      reason: 'loading',
      currentPlan,
      minPlan,
    };
  }

  const allowed = hasEntitlement(billing.plan_limits, capability);
  if (allowed) {
    return {
      allowed: true,
      isLoading: false,
      reason: 'allowed',
      currentPlan,
      minPlan,
    };
  }

  return {
    allowed: false,
    isLoading: false,
    reason: currentPlan ? 'feature_not_in_plan' : 'no_subscription',
    currentPlan,
    minPlan,
  };
}
